import { chain, map } from 'fp-ts/lib/TaskEither';
import { flow, pipe } from 'fp-ts/lib/function';
import {
  checklistCodec,
  listCodec,
  titleItemCodec,
  translationLanguagesResponseCodec,
} from './codec';
import {
  authHeaders,
  createAuthorizedDeleteRequest,
  createAuthorizedGetRequest,
  createAuthorizedPutRequest,
  createPostRequest,
  decode,
  fetchApi,
  formMultipartHeaders,
  setBody,
  setBodyFormData,
  setQuery,
} from '../..';
import { ChecklistQuery, encodeChecklistQuery } from './query';
import * as q from '@/services/api/request/query';
import { filterCodec } from '@/services/api/resources/filter';
import { resourceBulkDelete } from '@/services/api/resources/bulkDelete';
import { ManualTranslationsPayload } from '@/entities/checklist';

const baseUrl = '/v2/checklists';
const filterUrl = '/v2/filters/checklists';
const recordUrl = (id: number) => `${baseUrl}/${id}`;
const titleUrl = (id: number) => `${recordUrl(id)}/title`;
const languagesUrl = (id: number) => `${recordUrl(id)}/languages`;
const statusUrl = (id: number) => `${recordUrl(id)}/status`;
const translationsUrl = (checklistId: number, languageId: number) =>
  `${baseUrl}/${checklistId}/languages/${languageId}/translations`;

const legacyBaseUrl = '/nova-api/checklists';
const legacyEditUrl = (id: number) => `${legacyBaseUrl}/${id}/edit`;
const legacyConductUrl = (id: number) => `${legacyBaseUrl}/${id}/conduct`;
const legacyCreateUrl = `${legacyBaseUrl}/create`;
const legacyUpdateUrl = (id: number) => `${legacyBaseUrl}/${id}`;

export const list = (query: ChecklistQuery) => pipe(
  baseUrl,
  createAuthorizedGetRequest,
  map(setQuery(encodeChecklistQuery(query))),
  chain(fetchApi),
  chain(decode(listCodec)),
);

export const filter = (preventEmptyTitle = false) => pipe(
  filterUrl,
  createAuthorizedGetRequest,
  map(setQuery({ preventEmptyTitle: q.boolean(preventEmptyTitle) })),
  chain(fetchApi),
  chain(decode(filterCodec)),
);

export const title = (checklistId: number) => pipe(
  titleUrl(checklistId),
  createAuthorizedGetRequest,
  chain(fetchApi),
  chain(decode(titleItemCodec)),
);

//create uses GET here! nice...
export const create = (query?: { fromTemplate: number }) => pipe(
  legacyCreateUrl,
  createAuthorizedGetRequest,
  map(setQuery({ fromTemplate: query?.fromTemplate ? q.number(query.fromTemplate) : '' })),
  chain(fetchApi),
);

export const get = (id: number, languageId?: number) => pipe(
  id,
  legacyEditUrl,
  createAuthorizedGetRequest,
  map(setQuery(languageId ? { languageId: q.number(languageId) } : {})),
  chain(fetchApi),
);

export const legacyConduct = flow(
  legacyConductUrl,
  createAuthorizedGetRequest,
  chain(fetchApi),
  chain(decode(checklistCodec)),
);

export const bulkDelete = (records: number[]) => resourceBulkDelete(records, baseUrl);

export const update = (formData: FormData, id?: number) => pipe(
  id,
  () => id ? legacyUpdateUrl(id) : legacyBaseUrl,
  //so it sends POST request with _method='PUT' in FormData. nice...
  authHeaders(formMultipartHeaders(createPostRequest)),
  map(setBodyFormData(formData)),
  chain(fetchApi),
);

export const remove = flow(
  recordUrl,
  createAuthorizedDeleteRequest,
  chain(fetchApi),
);

export const getLanguages = (checklistId: number) => pipe(
  languagesUrl(checklistId),
  createAuthorizedGetRequest,
  chain(fetchApi),
  chain(decode(translationLanguagesResponseCodec)),
);

export const updateStatus = (checklistId: number, status: number) => pipe(
  statusUrl(checklistId),
  createAuthorizedPutRequest,
  map(setBody({ status })),
  chain(fetchApi),
);

export const updateTranslations = (
  checklistId: number,
  languageId: number,
  payload: ManualTranslationsPayload,
) => pipe(
  translationsUrl(checklistId, languageId),
  createAuthorizedPutRequest,
  map(setBody(payload)),
  chain(fetchApi),
);
